// src/components/dentally/DentallyBookingFormPatient.jsx
import PropTypes from 'prop-types';
import { Box, Typography, TextField, Chip, Divider, MenuItem } from '@mui/material';
import Iconify from '../Iconify';
import { sectionLabelStyle } from '../../styles/DentallyBookingStyle';

const TITLE_OPTIONS = ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr'];
const SEX_OPTIONS = ['Male', 'Female', 'Unknown'];

// Dentally patient status badge (existing leads only).
// Checks lead_metadata for { key: 'dentally_patient_id' }.
function DentallyPatientBadge({ leadMetadata }) {
  const patientEntry = (leadMetadata || []).find(m => m.key === 'dentally_patient_id');
  const patientId = patientEntry?.value;

  if (patientId != null && patientId !== '') {
    return (
      <Chip
        icon={<Iconify icon="mdi:check-circle" width={16} height={16} />}
        label={`Already in Dentally · Patient #${patientId}`}
        size="small"
        sx={{ bgcolor: 'rgba(0,171,85,0.12)', color: '#00AB55', fontWeight: 600, mt: 1 }}
      />
    );
  }

  return (
    <Chip
      icon={<Iconify icon="mdi:information-outline" width={16} height={16} />}
      label="Will be added to Dentally on booking"
      size="small"
      sx={{ bgcolor: 'rgba(7,141,238,0.10)', color: '#078DEE', fontWeight: 500, mt: 1 }}
    />
  );
}

DentallyPatientBadge.propTypes = {
  leadMetadata: PropTypes.array,
};

DentallyPatientBadge.defaultProps = {
  leadMetadata: [],
};

export default function DentallyBookingFormPatient({ existingLead, values, onChange, errors }) {
  // Existing lead → collapsed read-only pill
  if (existingLead) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography sx={sectionLabelStyle}>PATIENT</Typography>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
          bgcolor: 'action.hover', borderRadius: 1, mt: 0.5,
        }}>
          <Iconify icon="mdi:account" width={20} height={20} sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" fontWeight={600}>
            {[existingLead.first_name, existingLead.last_name].filter(Boolean).join(' ') || 'Unknown Patient'}
          </Typography>
          {existingLead.lead_contact?.phone && (
            <Typography variant="caption" color="text.secondary">
              · {existingLead.lead_contact.phone}
            </Typography>
          )}
        </Box>
        <DentallyPatientBadge leadMetadata={existingLead.lead_metadata} />
        {!existingLead.dob && (
          <TextField
            label="Date of birth"
            size="small"
            type="date"
            required
            sx={{ mt: 1.5, width: 200 }}
            value={values.dob || ''}
            onChange={e => onChange('dob', e.target.value)}
            error={!!errors.dob}
            helperText={errors.dob || 'Required by Dentally'}
            InputLabelProps={{ shrink: true }}
          />
        )}
        <Divider sx={{ mt: 2 }} />
      </Box>
    );
  }

  // New lead → editable fields
  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={sectionLabelStyle}>PATIENT</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1 }}>
        <TextField
          label="First name"
          size="small"
          required
          value={values.firstName || ''}
          onChange={e => onChange('firstName', e.target.value)}
          error={!!errors.firstName}
          helperText={errors.firstName}
        />
        <TextField
          label="Last name"
          size="small"
          required
          value={values.lastName || ''}
          onChange={e => onChange('lastName', e.target.value)}
          error={!!errors.lastName}
          helperText={errors.lastName}
        />
        <TextField
          label="Phone"
          size="small"
          type="tel"
          required
          value={values.phone || ''}
          onChange={e => onChange('phone', e.target.value)}
          error={!!errors.phone}
          helperText={errors.phone}
        />
        <TextField
          label="Email"
          size="small"
          type="email"
          required
          value={values.email || ''}
          onChange={e => onChange('email', e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
        />
        <TextField
          select
          label="Title"
          size="small"
          required
          value={values.salutation || ''}
          onChange={e => onChange('salutation', e.target.value)}
          error={!!errors.salutation}
          helperText={errors.salutation}
        >
          {TITLE_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField
          label="Date of birth"
          size="small"
          type="date"
          required
          value={values.dob || ''}
          onChange={e => onChange('dob', e.target.value)}
          error={!!errors.dob}
          helperText={errors.dob}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          select
          label="Gender"
          size="small"
          required
          value={values.sex || ''}
          onChange={e => onChange('sex', e.target.value)}
          error={!!errors.sex}
          helperText={errors.sex}
        >
          {SEX_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField
          label="Address line 1"
          size="small"
          required
          value={values.addressLine1 || ''}
          onChange={e => onChange('addressLine1', e.target.value)}
          error={!!errors.addressLine1}
          helperText={errors.addressLine1}
          sx={{ gridColumn: '1 / -1' }}
        />
        <TextField
          label="Postcode"
          size="small"
          required
          value={values.postcode || ''}
          onChange={e => onChange('postcode', e.target.value.toUpperCase())}
          error={!!errors.postcode}
          helperText={errors.postcode}
        />
      </Box>
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}

DentallyBookingFormPatient.propTypes = {
  existingLead: PropTypes.object,
  values: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

DentallyBookingFormPatient.defaultProps = {
  existingLead: null,
  errors: {},
};
